import DynamicForm, { FormValuesOutput } from '@policygenius/mortar-dynamic-forms';
import {
  useCreateInterviewSectionData,
  useFetchInterviewSectionAndData,
} from 'api/hooks/SequentialInterviews';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { completedInterviewStep, startedInterviewStep } from 'analytics/navigator';
import DynamicFormWrapper from 'components/DynamicFormWrapper';
import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { addGlobalError } from 'containers/App/actions';
import useRedirect from 'containers/Interview/util/useRedirect';
import useEffectAfterMount from 'hooks/useEffectAfterMount';
import useSelector from 'store/useSelector';
import shortUrnToUrn from 'util/interviews/shortUrnToUrn';

const InterviewPage = () => {
  const activeUserUuid = useSelector((app) => app.activeUserUuid);
  // Component is expected to render via InterviewRouter which passes these URL params
  const { shortInterviewURN, shortSectionURN } = useParams();
  const interviewURN = shortUrnToUrn(shortInterviewURN!, 'interview');
  const sectionURN = shortUrnToUrn(shortSectionURN!, 'section');
  const dispatch = useDispatch();
  const redirect = useRedirect();
  const { data, loading, fetchInterviewSectionAndData } = useFetchInterviewSectionAndData({
    params: { interviewURN, sectionURN },
    onCompleted: (d) => {
      startedInterviewStep(`${d.interviewDisplayName} - ${d.schema.content}`, activeUserUuid);
    },
    lazy: false,
  });
  const { createInterviewDataFromSchema } = useCreateInterviewSectionData({
    params: { interviewURN, sectionURN },
  });

  useEffectAfterMount(() => {
    fetchInterviewSectionAndData();
  }, [interviewURN, sectionURN]);

  if (loading) {
    return <GlobalLoader />;
  }
  if (!data) {
    dispatch(addGlobalError());

    return <></>;
  }
  const { schema, formValues, interviewDisplayName } = data;
  const sectionName = schema.content;

  const onSubmit = async (values: FormValuesOutput) => {
    const response = await createInterviewDataFromSchema(values, schema);

    if (!response.data) {
      dispatch(addGlobalError());

      return;
    }
    completedInterviewStep(`${interviewDisplayName} - ${schema.content}`, activeUserUuid);
    const { url, isInternal } = response.data;

    return redirect({ url, isInternal });
  };

  return (
    <NavigatorContent>
      <DynamicFormWrapper interviewDisplayName={interviewDisplayName}>
        <DynamicForm
          initialValues={formValues}
          schema={schema}
          name={sectionName}
          onSubmit={onSubmit}
        />
      </DynamicFormWrapper>
    </NavigatorContent>
  );
};

export default InterviewPage;
